import {
	ProductType,
	FeatureTypes,
	PaymentIntent,
	PurchaseDetails,
	PaymentMethodId,
	PricePreviewInput,
	EntitlementResponse,
	ProductInvoicePreview,
	InvoiceHistoryResponse,
	EntitlementPlansResponse,
	SubscriptionHistoryResponse,
	SingleProductPurchaseDetails,
	ProductAutomatedSubscriptionChangesResponse,
	PromotionalEntitlementTrialRequest,
} from '@/components/Billing/billing-types';
import { Response } from '@/react_app/common/AppPagination';
import { BillingEmailsConfig, OrganizationSettings } from '@/react_app/components/BillingUsage/Settings/settings-types';
import {
	getProducts as getProductsAction,
	getInvoices as getInvoicesAction,
	getEntitlements as getEntitlementsAction,
	getCustomerData as getCustomerDataAction,
	setPaymentMethod as setPaymentMethodAction,
	getPaymentMethod as getPaymentMethodAction,
	getPaymentIntent as getPaymentIntentAction,
	setIsAnnuallyPlan as setIsAnnuallyPlanAction,
	getBillingPreview as getBillingPreviewAction,
	clearPricePreviews as clearPricePreviewsAction,
	upgradeBillingPlans as upgradeBillingPlansAction,
	deletePaymentMethod as deletePaymentMethodAction,
	getEntitlementPlans as getEntitlementPlansAction,
	purchaseSingleProduct as purchaseSingleProductAction,
	requestBetaPlansExtension as requestBetaPlansExtensionAction,
	startPromotionalEntitlementsTrial as startPromotionalEntitlementsTrialAction,
	getFeatureUsageHistory as getFeatureUsageHistoryAction,
	getSubscriptionHistory as getSubscriptionHistoryAction,
	cancelProductDowngrade as cancelProductDowngradeAction,
	getAccountSubscriptions as getAccountSubscriptionsAction,
	getOrganizationSettings as getOrganizationSettingsAction,
	setDefaultPaymentMethod as setDefaultPaymentMethodAction,
	saveBillingEmailsConfig as saveBillingEmailsConfigAction,
	saveOrganizationSettings as saveOrganizationSettingsAction,
	setOveragesEnabled as setOveragesEnabledAction,
	getProductAutomatedSubscriptionChanges as getProductAutomatedSubscriptionChangesAction,
} from '@/store/modules/billing/actions';
import { useStore } from '@/utils/store';

export const useBillingActions = () => {
	const { dispatch } = useStore();
	return {
		deletePaymentMethod: (paymentMethodId: PaymentMethodId) =>
			dispatch<{ paymentMethodId: PaymentMethodId }, void>(deletePaymentMethodAction, { paymentMethodId }),
		setDefaultPaymentMethod: (paymentMethodId: PaymentMethodId) =>
			dispatch<{ paymentMethodId: PaymentMethodId }, void>(setDefaultPaymentMethodAction, { paymentMethodId }),
		getProducts: () => dispatch<undefined, void>(getProductsAction, undefined),
		getOrganizationSettings: () => dispatch<undefined, void>(getOrganizationSettingsAction, undefined),
		getInvoices: () => dispatch<undefined, Response<InvoiceHistoryResponse>>(getInvoicesAction, undefined),
		getCustomerData: () => dispatch<undefined, void>(getCustomerDataAction, undefined),
		clearPricePreviews: () => dispatch<undefined, void>(clearPricePreviewsAction, undefined),
		setPaymentMethod: (payload: { paymentMethodId: string; name: string }) =>
			dispatch<{ paymentMethodId: string; name: string }, void>(setPaymentMethodAction, payload),
		getPaymentMethod: () => dispatch<undefined, void>(getPaymentMethodAction, undefined),
		upgradeBillingPlans: (payload: { purchaseDetails: PurchaseDetails; customerInvoiceName: string }) =>
			dispatch<{ purchaseDetails: PurchaseDetails }, void>(upgradeBillingPlansAction, payload),
		purchaseSingleProduct: (payload: { purchaseDetails: SingleProductPurchaseDetails }) =>
			dispatch<{ purchaseDetails: SingleProductPurchaseDetails }, void>(purchaseSingleProductAction, payload),
		requestBetaPlansExtension: () => dispatch<undefined, void>(requestBetaPlansExtensionAction, undefined),
		startPromotionalEntitlementsTrial: (payload: PromotionalEntitlementTrialRequest) =>
			dispatch<PromotionalEntitlementTrialRequest, void>(startPromotionalEntitlementsTrialAction, payload),
		setOveragesEnabled: (payload: { isOveragesEnabled: boolean }) =>
			dispatch<{ isOveragesEnabled: boolean }, void>(setOveragesEnabledAction, payload),
		getAccountSubscriptions: () => dispatch<undefined, void>(getAccountSubscriptionsAction, undefined),
		setIsAnnuallyPlan: (isAnnuallyPlan: boolean) =>
			dispatch<{ isAnnuallyPlan: boolean }, void>(setIsAnnuallyPlanAction, { isAnnuallyPlan }),
		getPaymentIntent: () => dispatch<undefined, PaymentIntent>(getPaymentIntentAction, undefined),
		cancelProductDowngrade: (productType: ProductType) =>
			dispatch<{ productType: ProductType }, void>(cancelProductDowngradeAction, { productType }),
		getBillingPreview: (payload: { pricePreviewInput: PricePreviewInput }) =>
			dispatch<{ pricePreviewInput: PricePreviewInput }, ProductInvoicePreview>(getBillingPreviewAction, payload),
		saveBillingEmailsConfig: (payload: { billingEmailsConfig: BillingEmailsConfig }) =>
			dispatch<{ billingEmailsConfig: BillingEmailsConfig }, ProductInvoicePreview>(saveBillingEmailsConfigAction, payload),
		saveOrganizationSettings: (payload: { organizationSettings: OrganizationSettings }) =>
			dispatch<{ organizationSettings: OrganizationSettings }, ProductInvoicePreview>(saveOrganizationSettingsAction, payload),
		getEntitlements: (payload: { forceGet?: Boolean }) =>
			dispatch<{ forceGet?: Boolean }, EntitlementResponse[]>(getEntitlementsAction, payload),
		getEntitlementPlans: () => dispatch<undefined, EntitlementPlansResponse>(getEntitlementPlansAction, undefined),
		getFeatureUsageHistory: (payload: { featureId: FeatureTypes }) =>
			dispatch<{ featureId: FeatureTypes }, void>(getFeatureUsageHistoryAction, payload),
		getSubscriptionHistory: (payload: { productId: ProductType }) =>
			dispatch<{ productId: ProductType }, Response<SubscriptionHistoryResponse>>(getSubscriptionHistoryAction, payload),
		getProductAutomatedSubscriptionChanges: (payload: { productId: ProductType }) =>
			dispatch<{ productId: ProductType }, Response<ProductAutomatedSubscriptionChangesResponse>>(
				getProductAutomatedSubscriptionChangesAction,
				payload
			),
	};
};
